import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Joyride, { STATUS } from "react-joyride";
import "./SubjectManagement.css";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Alert,
} from "react-bootstrap";
import { Plus, Edit, Trash2, BookOpen } from "lucide-react";
import {
  fetchSubjects,
  createSubject,
  updateSubject,
  deleteSubject,
  createBulkSubjects,
  handleJoyRideRunning,
} from "./redux/subjectSlice";
import userStore from "../../../zustand/userStore"; // Assuming this path is correct
import "./SubjectManagement.css";
import { useNavigate } from "react-router-dom";
import { subjectSteps } from "../../../tour/timetableTourSteps";
import { createPortal } from "react-dom";
import {
  setFormValue,
  setStepIndex,
  startTour,
  stopTour,
} from "../../../redux/globalJoyrideSlice";
import GlobalJoyrideCloseButton from "../../../components/GlobalJoyrideCloseButton";
import { setAppdAiUser } from "../../../utils/appdAiHelpers";

const SubjectManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { subjects, loading, error } = useSelector((state) => state.subjects);

  // Get school_id from masterData slice
  const masterData = useSelector((state) => state.masterData);
  const schoolId = masterData?.school_id; // Using optional chaining for safety

  // Get user_id from Zustand store
  const userData = userStore((state) => state.user);
  const userId = userData?.id; // Assuming the user object has an 'id' property
  const adminId = userData?.admin_id;

  const [activeTab, setActiveTab] = useState(null);
  const [subjectCount, setSubjectCount] = useState("");
  const [bulkSubjects, setBulkSubjects] = useState([]);
  const [subjectInputValue, setSubjectInputValue] = useState("");
  const { sessionClosed } = useSelector((state) => state.globalJoyride);

  const handleSubjectCountChange = (value) => {
    const count = Number(value);

    if (count < 0 || count > 50) return; // safety limit

    setSubjectCount(value);

    const subjectsArray = Array.from({ length: count }, (_, index) => ({
      id: index + 1,
      name: "",
    }));

    setBulkSubjects(subjectsArray);
  };

  const handleBulkChange = (index, value) => {
    setBulkSubjects((prev) =>
      prev.map((item, i) => (i === index ? { ...item, name: value } : item)),
    );
  };

  // const addBulkField = () => {
  //   setBulkSubjects((prev) => [
  //     ...prev,
  //     { id: Date.now(), name: "" },
  //   ]);
  // };

  const removeBulkField = (id) => {
    setBulkSubjects((prev) => prev.filter((item) => item.id !== id));
  };

  const inputRef = useRef(null);

  // const { isJoyRideRunning } = useSelector((state) => state.subjects);

  // const isWalkthroughCompleted =
  //   localStorage.getItem("timetableTourCompleted") === "true";

  console.log("SubjectManagement - School ID:", schoolId);
  console.log("SubjectManagement - User ID:", userId);

  useEffect(() => {
    // Fetch subjects only if schoolId is available
    if (schoolId) {
      dispatch(fetchSubjects(schoolId)); // Pass schoolId as a parameter for fetching
    }
  }, [dispatch, schoolId]); // Add schoolId to dependencies

  useEffect(() => {
    if (!schoolId) return; // wait for data

    if (!sessionClosed) {
      // 👈 KEY LOGIC
      dispatch(
        startTour({
          name: "subject",
          steps: subjectSteps({
            dispatch,
            setShowModal,
            setFormData,
            setEditingSubject,
            navigate,
            stopTour: () => dispatch(stopTour()),
          }),
        }),
      );
    }
  }, [sessionClosed, schoolId, dispatch, navigate]);

  useEffect(() => {
    // if (userData) setAppdAiUser(userData, "Academic Calender View");
    if (userData) setAppdAiUser(userData, "demo");
  }, []);

  const [showModal, setShowModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [formData, setFormData] = useState({
    subject_name: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!schoolId || !userId) {
      alert("School ID or User ID is missing. Cannot perform this action.");
      console.error("Missing schoolId or userId:", { schoolId, userId });
      return;
    }

    try {
      /* ================= EDIT SUBJECT ================= */
      if (editingSubject) {
        const enteredName = formData.subject_name.trim().toLowerCase();

        const isDuplicate = subjects.some(
          (s) =>
            s.subject_name.trim().toLowerCase() === enteredName &&
            s.subject_id !== editingSubject.subject_id,
        );

        if (isDuplicate) {
          alert("A subject with the same name already exists.");
          return;
        }

        await dispatch(
          updateSubject({
            id: editingSubject.subject_id,
            data: {
              subject_name: formData.subject_name.trim(),
              user_id: userId,
            },
          }),
        ).unwrap();

        setShowModal(false);
        setFormData({ subject_name: "" });
        setEditingSubject(null);
        dispatch(fetchSubjects(schoolId));
        return;
      }

      /* ================= SINGLE SUBJECT ================= */
      if (activeTab === "single") {
        const enteredName = formData.subject_name.trim().toLowerCase();

        if (!enteredName) {
          alert("Subject name is required.");
          return;
        }

        const isDuplicate = subjects.some(
          (s) => s.subject_name.trim().toLowerCase() === enteredName,
        );

        if (isDuplicate) {
          alert("A subject with the same name already exists.");
          return;
        }

        await dispatch(
          createSubject({
            subject_name: formData.subject_name.trim(),
            school_id: schoolId,
            user_id: userId,
          }),
        ).unwrap();

        //navigate("/timetable/teachers");
        dispatch(setStepIndex(4));
      }

      /* ================= MULTIPLE SUBJECTS ================= */
      if (activeTab === "multiple") {
        const enteredSubjects = bulkSubjects
          .map((s) => s.name.trim())
          .filter(Boolean);

        if (!enteredSubjects.length) {
          alert("Please fill all subject names.");
          return;
        }

        await dispatch(
          createBulkSubjects({
            subjects: enteredSubjects,
            school_id: schoolId,
            user_id: userId,
          }),
        ).unwrap();

        dispatch(setStepIndex(4));
      }

      /* ================= CLEANUP ================= */
      setShowModal(false);
      setFormData({ subject_name: "" });
      setBulkSubjects([{ id: Date.now(), name: "" }]);
      setEditingSubject(null);
      dispatch(fetchSubjects(schoolId));
    } catch (submitError) {
      console.error("Error saving subject:", submitError);
      alert(
        `Error saving subject: ${submitError.message || "Please try again"}`,
      );
    }
  };

  const handleEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({
      subject_name: subject.subject_name,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    // Basic validation for userId
    if (!userId) {
      alert("User ID is missing. Cannot perform delete action.");
      console.error("Missing userId for delete:", { userId });
      return;
    }

    if (window.confirm("Are you sure you want to delete this subject?")) {
      try {
        // For delete, send subject_id and user_id
        await dispatch(deleteSubject({ id: id, user_id: userId })).unwrap();
        // Re-fetch subjects after successful delete
        if (schoolId) {
          dispatch(fetchSubjects(schoolId));
        }
      } catch (deleteError) {
        console.error("Error deleting subject:", deleteError);
        alert(
          `Error deleting subject: ${deleteError.message || "Please try again"}`,
        );
      }
    }
  };

  return (
    <div className="px-5 subject-management-wrapper">
      <div id="joyride-confirm-anchor" />
      {/* <GlobalJoyrideCloseButton /> */}
      <Row className="py-3 px-2">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            {/* <div>
              <h2 className="text-primary fw-bold mb-0">Subject Management</h2>
              <p className="text-muted">Manage all school subjects and curriculum</p>
            </div> */}
            <Button
              id="add-subject-btn"
              variant="btn btn-primary btn-sm"
              onClick={() => {
                setEditingSubject(null);
                setFormData({ subject_name: "" });
                setShowModal(true);
                dispatch(setStepIndex(1));
                setTimeout(() => {
                  if (inputRef.current) inputRef.current.focus();
                }, 0);
              }}
              className="d-flex align-items-center mobile-add-subject-btn"
            >
              <Plus
                size={window.innerWidth <= 768 ? 14 : 20}
                className="me-1 me-md-2"
              />
              Add Subject
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      <div className="px-2">
        <Card>
          <Card.Header>
            <h5 className="mb-0 d-flex align-items-center">
              <BookOpen size={24} className="me-2" />
              All Subjectssssss
            </h5>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center py-4">
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <>
                {subjects.length >= 10 ? (
                  <Row>
                    {[0, 1].map((colIndex) => {
                      const start = colIndex * Math.ceil(subjects.length / 2);
                      const end = start + Math.ceil(subjects.length / 2);
                      const columnSubjects = subjects.slice(start, end);

                      return (
                        <Col md={6} key={colIndex}>
                          <Table responsive hover className="mb-0">
                            <thead>
                              <tr>
                                <th>Subject Name</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {columnSubjects.map((subject) => (
                                <tr key={subject.subject_id}>
                                  <td className="fw-semibold">
                                    {subject.subject_name}
                                  </td>
                                  <td>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="me-1 p-1 text-primary"
                                      onClick={() => handleEdit(subject)}
                                    >
                                      <Edit size={16} />
                                    </Button>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-1 text-danger"
                                      onClick={() =>
                                        handleDelete(subject.subject_id)
                                      }
                                    >
                                      <Trash2 size={16} />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Col>
                      );
                    })}
                  </Row>
                ) : (
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Subject Name</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subjects.length > 0 ? (
                        subjects.map((subject) => (
                          <tr key={subject.subject_id}>
                            <td className="fw-semibold">
                              {subject.subject_name}
                            </td>
                            <td>
                              <Button
                                variant="link"
                                size="sm"
                                className="me-1 p-1 text-primary"
                                onClick={() => handleEdit(subject)}
                              >
                                <Edit size={16} />
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-1 text-danger"
                                onClick={() => handleDelete(subject.subject_id)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="2"
                            className="text-center text-muted py-3"
                          >
                            No subjects found for this school.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingSubject ? "Edit Subject" : "Add New Subject"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {/* TAB SWITCH */}
            {!editingSubject && (
              <div className="d-flex gap-2 mb-4" id="single-subject-tab">
                <Button
                  size="sm"
                  variant={
                    activeTab === "single" ? "primary" : "outline-primary"
                  }
                  onClick={() => {
                    setActiveTab("single");
                    setSubjectCount("");
                    setBulkSubjects([]);
                    dispatch(setStepIndex(2));
                  }}
                >
                  Single Subject
                </Button>

                <Button
                  size="sm"
                  variant={
                    activeTab === "multiple" ? "primary" : "outline-primary"
                  }
                  onClick={() => {
                    setActiveTab("multiple");
                    setFormData({ subject_name: "" });
                    dispatch(setStepIndex(2));
                  }}
                >
                  Multiple Subjects
                </Button>
              </div>
            )}

            {/* SINGLE SUBJECT */}
            {(activeTab === "single" || editingSubject) && (
              <Form.Group className="mb-3" id="subject-name-input">
                <Form.Label>Subject Name</Form.Label>
                <Form.Control
                  id="subject-name-input"
                  type="text"
                  value={formData.subject_name}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData({ ...formData, subject_name: val });
                    dispatch(
                      setFormValue({
                        // ✅ SYNC WITH JOYRIDE
                        key: "subjectName",
                        value: val,
                      }),
                    );
                  }}
                  placeholder="e.g., Mathematics"
                  required
                />
              </Form.Group>
            )}

            {/* MULTIPLE SUBJECTS */}
            {activeTab === "multiple" && !editingSubject && (
              <>
                {/* NUMBER INPUT */}
                <Form.Group className="mb-3" id="subject-name-input">
                  <Form.Label>Number of Subjects</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="50"
                    value={subjectCount}
                    onChange={(e) => {
                      const val = e.target.value;
                      handleSubjectCountChange(val);
                      dispatch(
                        setFormValue({
                          // ✅ SYNC WITH JOYRIDE
                          key: "subjectName",
                          value: val,
                        }),
                      );
                    }}
                    placeholder="Enter number (e.g., 5)"
                  />
                </Form.Group>

                {/* GENERATED INPUTS */}
                {bulkSubjects.length > 0 &&
                  bulkSubjects.map((item, index) => (
                    <Form.Group key={item.id} className="mb-2">
                      <Form.Control
                        type="text"
                        placeholder={`Subject ${index + 1}`}
                        value={item.name}
                        onChange={(e) =>
                          handleBulkChange(index, e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  ))}
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>

            <Button
              id="create-subject-btn"
              variant="primary"
              type="submit"
              disabled={loading}
            >
              {editingSubject
                ? "Update Subject"
                : activeTab === "single"
                  ? "Create Subject"
                  : "Create Subjects"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default SubjectManagement;
